import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { updateMe, apiClient, uploadResume, skillGap, compositeScore, verifyEmailSend, verifyEmailConfirm, verifyPhoneSend, verifyPhoneConfirm } from "@/lib/api";
import {
  ArrowRight, Loader2, Linkedin, Sparkles, Target, FileText, Upload,
  Zap, CheckCircle2, Award, Check, SkipForward, Mail, Phone, ShieldCheck,
} from "lucide-react";
import ScoreRing from "@/components/wishire/ScoreRing";

/**
 * Guided candidate activation wizard.
 * role → LinkedIn → résumé → skills → reveal. Each step returns instant value;
 * the heavy step (voice interview) is deferred to the dashboard.
 */
const STEPS = ["role", "linkedin", "resume", "skills", "reveal"];
const STEP_LABEL = { role: "Your goal", linkedin: "LinkedIn", resume: "Résumé", skills: "Skills", reveal: "Your score" };

export default function CandidateProfileSetup() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("role");

  // step 1
  const [targetRole, setTargetRole] = useState("");
  const [course, setCourse] = useState(user?.branch || user?.course || "");
  const [phone, setPhone] = useState("");
  const [emailVerified, setEmailVerified] = useState(!!user?.email_verified);
  const [linkedinUrl, setLinkedinUrl] = useState("");
  // results
  const [li, setLi] = useState(null);
  const [resume, setResume] = useState(null);
  const [skills, setSkills] = useState(null);
  const [composite, setComposite] = useState(null);
  // ui
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const stepIndex = STEPS.indexOf(step);
  const validLinkedIn = /linkedin\.com\/(in|pub)\//i.test(linkedinUrl);
  const go = (s) => { setError(null); setStep(s); };

  // ── Step 1: goal ──
  const saveGoal = async (e) => {
    e?.preventDefault();
    if (!targetRole.trim()) { setError("Tell us the role you're aiming for."); return; }
    if (!emailVerified) { setError("Please verify your email to continue."); return; }
    setBusy(true); setError(null);
    try {
      await updateMe({ target_role: targetRole.trim(), phone: phone.trim() || undefined });
      await refresh();
      go("linkedin");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save.");
    } finally { setBusy(false); }
  };

  // ── Step 2: LinkedIn ──
  const analyzeLinkedIn = async () => {
    if (!validLinkedIn) { setError("Paste a real LinkedIn URL (e.g. linkedin.com/in/yourname)."); return; }
    setBusy(true); setError(null);
    try {
      await updateMe({ linkedin_url: linkedinUrl.trim() });
      const { data } = await apiClient.post("/linkedin/analyze", { url: linkedinUrl.trim() });
      setLi(data);
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Analysis failed — make sure the profile is public.");
    } finally { setBusy(false); }
  };

  // ── Step 3: résumé ──
  const onResumeFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const { data } = await uploadResume(file, targetRole.trim() || undefined);
      setResume(data.resume?.result || null);
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't read that file. Try a PDF or DOCX.");
    } finally { setBusy(false); }
  };

  // ── Step 4: skills (auto) ──
  const loadSkills = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await skillGap();
      setSkills(data);
    } catch (err) {
      setSkills(null);
    } finally { setBusy(false); }
  };

  // ── Step 5: reveal ──
  const goReveal = async () => {
    setBusy(true);
    try {
      const { data } = await compositeScore();
      setComposite(data);
    } catch (_) {} finally { setBusy(false); go("reveal"); }
  };

  const finish = async () => {
    setBusy(true);
    try {
      await updateMe({ onboarding_step: "done" });
      await refresh();
      navigate("/app", { replace: true });
    } catch (e) { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-10 md:py-14">
      <div className="container-x max-w-2xl">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-10">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1.5 rounded-full transition-colors ${i <= stepIndex ? "bg-[#C2410C]" : "bg-stone-200"}`} />
              <span className={`mt-1.5 block text-[10px] font-mono uppercase tracking-widest ${i === stepIndex ? "text-[#C2410C]" : "text-stone-400"}`}>
                {STEP_LABEL[s]}
              </span>
            </div>
          ))}
        </div>

        <div>
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* ───────── Step 1: goal ───────── */}
            {step === "role" && (
              <>
                <Icon><Target size={22} /></Icon>
                <h1 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
                  What role are you <span className="italic text-stone-500">aiming for?</span>
                </h1>
                <p className="mt-3 text-stone-600">Not sure yet? That's normal for a fresher — keep it broad (e.g. "Any good fresher role") and we'll plan around your course and the companies at your campus.</p>
                <form onSubmit={saveGoal} className="mt-8 space-y-4">
                  <input
                    autoFocus type="text" data-testid="ob-role" value={targetRole}
                    onChange={(e) => setTargetRole(e.target.value)}
                    placeholder="e.g. Software Developer, Marketing Associate, Finance Analyst, HR"
                    className="w-full px-5 py-4 rounded-2xl bg-white border-2 border-stone-200 focus:outline-none focus:border-stone-900 text-base"
                  />
                  <EmailVerify email={user?.email} verified={emailVerified} onVerified={() => setEmailVerified(true)} />
                  <input
                    type="tel" data-testid="ob-phone" value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Phone (optional — for opportunities)"
                    className="w-full px-5 py-3.5 rounded-2xl bg-white border-2 border-stone-200 focus:outline-none focus:border-stone-900 text-sm"
                  />
                  {error && <Err>{error}</Err>}
                  <button type="submit" disabled={busy || !emailVerified} data-testid="ob-role-next" className="btn-primary w-full justify-center disabled:opacity-60">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <>Continue <ArrowRight size={16} /></>}
                  </button>
                </form>
              </>
            )}

            {/* ───────── Step 2: LinkedIn ───────── */}
            {step === "linkedin" && (
              <>
                <Icon color="#0a66c2"><Linkedin size={22} /></Icon>
                <h1 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
                  Score your <span className="italic text-stone-500">LinkedIn.</span>
                </h1>
                <p className="mt-3 text-stone-600">Paste your URL — we grade it instantly and pre-fill your profile.</p>

                {!li ? (
                  <div className="mt-8 space-y-4">
                    <input
                      autoFocus type="url" data-testid="ob-linkedin" value={linkedinUrl}
                      onChange={(e) => setLinkedinUrl(e.target.value)}
                      placeholder="https://linkedin.com/in/yourname"
                      className="w-full px-5 py-4 rounded-2xl bg-white border-2 border-stone-200 focus:outline-none focus:border-stone-900 text-base"
                    />
                    {error && <Err>{error}</Err>}
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={analyzeLinkedIn} disabled={busy} data-testid="ob-linkedin-run" className="btn-primary disabled:opacity-60">
                        {busy ? <><Loader2 size={16} className="animate-spin" /> Analyzing…</> : <>Score it <Sparkles size={16} /></>}
                      </button>
                      <SkipBtn onClick={() => go("resume")} />
                    </div>
                  </div>
                ) : (
                  <MiniResult
                    score={li.score} label={li.verdict}
                    tip={(li.suggested_improvements || li.gaps || [])[0]}
                    onNext={() => go("resume")}
                  />
                )}
              </>
            )}

            {/* ───────── Step 3: résumé ───────── */}
            {step === "resume" && (
              <>
                <Icon color="#D97706"><FileText size={22} /></Icon>
                <h1 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
                  Add your <span className="italic text-stone-500">résumé.</span>
                </h1>
                <p className="mt-3 text-stone-600">Your résumé is the first of three signals in your Wishire Score. We grade it and extract your skills.</p>

                {!resume ? (
                  <div className="mt-8 space-y-4">
                    <label className="flex flex-col items-center justify-center gap-3 px-6 py-12 rounded-2xl border-2 border-dashed border-stone-300 bg-white cursor-pointer hover:border-stone-500 transition-colors">
                      {busy ? (
                        <><Loader2 size={28} className="animate-spin text-stone-400" /><span className="text-sm text-stone-500">Scoring your résumé…</span></>
                      ) : (
                        <>
                          <Upload size={28} className="text-stone-400" />
                          <span className="text-sm font-medium text-stone-700">Click to upload — PDF or DOCX</span>
                          <span className="text-xs text-stone-400">Max 5MB</span>
                        </>
                      )}
                      <input type="file" accept=".pdf,.doc,.docx" data-testid="ob-resume-file" onChange={onResumeFile} disabled={busy} className="hidden" />
                    </label>
                    {error && <Err>{error}</Err>}
                    <div className="rounded-2xl border border-[#C2410C]/30 bg-amber-50/60 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="ob-build-resume">
                      <div><p className="text-sm font-medium text-stone-900">Don't have one yet?</p><p className="text-xs text-stone-600">We pre-fill your details and ask five quick questions. Takes about five minutes, and your first build is free.</p></div>
                      <button type="button" disabled={busy} onClick={async () => { setBusy(true); try { await updateMe({ onboarding_step: "done" }); navigate("/app/resume?tab=build", { replace: true }); } catch (_) { setBusy(false); } }} className="px-4 py-2 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412]">Build one in 5 minutes</button>
                    </div>
                    <SkipBtn onClick={() => go("skills")} label="Skip — I'll add it later" />
                  </div>
                ) : (
                  <MiniResult
                    score={resume.overall_score} label={resume.verdict || "Scored"}
                    tip={(resume.gaps || resume.suggestions || [])[0]}
                    onNext={() => { setSkills(null); go("skills"); loadSkills(); }}
                  />
                )}
              </>
            )}

            {/* ───────── Step 4: skills ───────── */}
            {step === "skills" && (
              <>
                <Icon color="#15803D"><Zap size={22} /></Icon>
                <h1 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
                  Your <span className="italic text-stone-500">skill gaps.</span>
                </h1>
                <p className="mt-3 text-stone-600">Here's how your skills line up with {targetRole || "your target role"}.</p>

                {busy && !skills ? (
                  <div className="mt-10 flex items-center gap-3 text-stone-500"><Loader2 size={20} className="animate-spin" /> Analyzing your skills…</div>
                ) : skills ? (
                  <div className="mt-8 rounded-2xl border border-stone-200 bg-white p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="overline">Coverage</span>
                      <span className="font-display text-2xl text-stone-900">{skills.coverage_percent ?? 0}%</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-stone-100 overflow-hidden mb-5">
                      <div className="h-full rounded-full bg-emerald-500" style={{ width: `${skills.coverage_percent ?? 0}%` }} />
                    </div>
                    {skills.top_skill_to_learn_next && (
                      <p className="text-sm text-stone-700 mb-3">
                        <span className="font-medium">Learn next:</span> {skills.top_skill_to_learn_next}
                      </p>
                    )}
                    {(skills.missing_must_haves || []).length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {(skills.missing_must_haves || []).slice(0, 6).map((s) => (
                          <span key={s} className="px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium">{s}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-8 rounded-2xl border border-stone-200 bg-white p-6" data-testid="ob-course-confirm">
                    <p className="text-sm font-medium text-stone-900">Nothing to analyse yet — so let's confirm your course instead.</p>
                    <p className="text-xs text-stone-600 mt-1">Your plan, courses and drill questions are built around it. Change it any time on your profile.</p>
                    <label className="block mt-4 text-xs text-stone-500">Course / branch
                      <input list="ob-courses" value={course} onChange={(e) => setCourse(e.target.value)} placeholder="e.g. BCA, BBA, B.Com, B.Tech CSE" data-testid="ob-course"
                        className="mt-1 w-full px-4 py-3 rounded-xl bg-[#FDFBF7] border border-stone-300 text-sm focus:outline-none focus:border-stone-900" />
                      <datalist id="ob-courses">{["B.Tech CSE", "B.Tech IT", "B.Tech ECE", "B.Tech EE", "B.Tech Mechanical", "B.Tech Civil", "BCA", "MCA", "B.Sc Computer Science", "B.Sc IT", "B.Sc", "BBA", "MBA", "PGDM", "B.Com", "B.Com (Hons)", "M.Com", "BMS", "BA", "BA (Hons) Economics", "MA", "B.Ed", "B.Pharm", "BHM (Hotel Management)", "LLB"].map((c) => <option key={c} value={c} />)}</datalist>
                    </label>
                  </div>
                )}

                <button type="button" onClick={async () => { if (!skills && course.trim() && course.trim() !== (user?.branch || "")) { try { await updateMe({ branch: course.trim() }); } catch (_) {} } goReveal(); }} disabled={busy} data-testid="ob-skills-next" className="btn-primary mt-6 disabled:opacity-60">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <>See my Wishire Score <ArrowRight size={16} /></>}
                </button>
              </>
            )}

            {/* ───────── Step 5: reveal ───────── */}
            {step === "reveal" && (
              <div className="text-center">
                <Icon center color="#C2410C"><Award size={22} /></Icon>
                <p className="overline mt-4">Your Wishire Score</p>
                <div className="mt-6 flex justify-center">
                  <ScoreRing value={composite?.score ?? 0} size={220} />
                </div>
                <h1 className="font-display text-3xl md:text-4xl mt-6 leading-tight">
                  You're set up, <span className="italic text-stone-500">{(targetRole && !/not sure|any |don.t know|undecided/i.test(targetRole)) ? "future " + targetRole : (user?.name || "").split(" ")[0] || "let's go"}.</span>
                </h1>

                {/* what's contributing */}
                <div className="mt-6 grid grid-cols-3 gap-2 text-left">
                  <Contrib done={!!li} label="LinkedIn" />
                  <Contrib done={!!resume} label="Résumé" />
                  <Contrib done={false} label="Interview" pending />
                </div>

                {/* deferred interview nudge */}
                <div className="mt-6 rounded-2xl bg-gradient-to-r from-[#fff7ed] to-[#fefce8] border border-[#C2410C]/25 p-5 text-left flex items-center gap-4">
                  <span className="inline-flex p-2.5 rounded-xl bg-[#C2410C] text-white shrink-0"><Sparkles size={18} /></span>
                  <div className="flex-1">
                    <p className="font-medium text-stone-900">Run a 5-min mock interview to add up to +40</p>
                    <p className="text-sm text-stone-600">It's your highest-weighted signal. We'll have it ready on your dashboard.</p>
                  </div>
                </div>

                <button type="button" onClick={finish} disabled={busy} data-testid="ob-finish" className="btn-primary w-full justify-center mt-6 disabled:opacity-60">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <>Open my dashboard <ArrowRight size={16} /></>}
                </button>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

/* ── sub-components ── */
function Icon({ children, color = "#1C1917", center }) {
  return (
    <span className={`inline-flex p-3 rounded-2xl text-white ${center ? "mx-auto" : ""}`} style={{ background: color }}>
      {children}
    </span>
  );
}
function Err({ children }) {
  return <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-2" data-testid="ob-error">{children}</p>;
}
function SkipBtn({ onClick, label = "Skip for now" }) {
  return (
    <button type="button" onClick={onClick} className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-900 transition-colors">
      {label} <SkipForward size={14} />
    </button>
  );
}
function MiniResult({ score, label, tip, onNext }) {
  return (
    <div className="mt-8">
      <div className="rounded-2xl bg-stone-900 text-stone-50 p-6 flex items-center gap-5">
        <ScoreRing value={score ?? 0} size={96} dark />
        <div className="min-w-0">
          <p className="font-display text-2xl">{score ?? 0}<span className="text-stone-400 text-base">/100</span> · <span className="text-amber-300">{label}</span></p>
          {tip && <p className="text-sm text-stone-300 mt-1 leading-snug">Top fix: {tip}</p>}
        </div>
      </div>
      <button type="button" onClick={onNext} data-testid="ob-mini-next" className="btn-primary mt-5">
        Continue <ArrowRight size={16} />
      </button>
    </div>
  );
}
function Contrib({ done, label, pending }) {
  return (
    <div className={`rounded-xl border p-3 flex flex-col items-center gap-1 ${done ? "border-emerald-200 bg-emerald-50" : pending ? "border-amber-200 bg-amber-50" : "border-stone-200 bg-stone-50"}`}>
      {done ? <Check size={16} className="text-emerald-600" /> : <CheckCircle2 size={16} className={pending ? "text-amber-500" : "text-stone-300"} />}
      <span className="text-xs font-medium text-stone-700">{label}</span>
    </div>
  );
}

/* ── Email verification (OTP to the signup email) ── */
function EmailVerify({ email, verified, onVerified }) {
  const [sent, setSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const send = async () => {
    setBusy(true); setMsg("");
    try { await verifyEmailSend(); setSent(true); setMsg("Code sent to your email."); }
    catch (e) { setMsg(e?.response?.data?.detail || "Could not send code."); }
    finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true); setMsg("");
    try { await verifyEmailConfirm(otp.trim()); onVerified?.(); }
    catch (e) { setMsg(e?.response?.data?.detail || "Incorrect code."); }
    finally { setBusy(false); }
  };

  return (
    <div className={`rounded-2xl border-2 p-4 ${verified ? "border-emerald-200 bg-emerald-50" : "border-stone-200 bg-white"}`} data-testid="ob-email-verify">
      <div className="flex items-center gap-2.5">
        <Mail size={16} className={verified ? "text-emerald-600" : "text-stone-500"} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-stone-900 truncate">{email || "Your email"}</p>
          <p className="text-xs text-stone-500">{verified ? "Verified" : "Verify your email address"}</p>
        </div>
        {verified ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><ShieldCheck size={14} /> Verified</span>
        ) : !sent ? (
          <button type="button" onClick={send} disabled={busy} data-testid="ob-email-send" className="text-sm font-medium px-3.5 py-1.5 rounded-full bg-stone-900 text-white hover:bg-[#C2410C] disabled:opacity-60">
            {busy ? <Loader2 size={13} className="animate-spin" /> : "Verify"}
          </button>
        ) : null}
      </div>
      {!verified && sent && (
        <div className="mt-3 flex items-center gap-2">
          <input value={otp} onChange={(e) => setOtp(e.target.value)} inputMode="numeric" maxLength={6} placeholder="6-digit code" data-testid="ob-email-otp"
            className="flex-1 px-3 py-2 rounded-xl border border-stone-300 text-sm font-mono tracking-widest focus:outline-none focus:border-stone-900" />
          <button type="button" onClick={confirm} disabled={busy || otp.length < 4} data-testid="ob-email-confirm" className="text-sm font-medium px-3.5 py-2 rounded-full bg-[#C2410C] text-white hover:bg-[#9a3412] disabled:opacity-50">Confirm</button>
          <button type="button" onClick={send} disabled={busy} className="text-xs text-stone-400 hover:text-stone-700">Resend</button>
        </div>
      )}
      {!verified && msg && <p className="mt-2 text-xs text-stone-500">{msg}</p>}
    </div>
  );
}

